import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { FaTimes, FaPlus, FaEdit } from "react-icons/fa";
import { createSalesManual, getSalesManual, updateSalesManual, getSalesTags } from "JS/API";
import { catchError } from "JS/common";
import CommonModal from "Components/CommonModal";
import SalesTagSelect from "Components/Sales/SalesTagSelect";
import unwrapResponse from "utils/unwrapResponse";

const CHANNEL_TO_TAG_CODE = {
  kmong: "kmong",
  bank_transfer: "bank_transfer",
};

// 결제 채널 옵션
const CHANNEL_OPTIONS = [
  { value: "", label: "선택 안함" },
  { value: "kmong", label: "크몽" },
  { value: "bank_transfer", label: "통장입금" },
  { value: "other", label: "기타" }
];

// 서비스 구분 옵션
const SERVICE_TYPE_OPTIONS = [
  { value: "trademark", label: "상표" },
  { value: "patent", label: "특허" },
  { value: "design", label: "디자인" },
  { value: "other", label: "기타" }
];

// 날짜 포맷 헬퍼
const formatDateDisplay = (dateString) => {
  const [year, month, day] = dateString.split('-');
  return `${year}년 ${parseInt(month)}월 ${parseInt(day)}일`;
};

// 스타일 정의
const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 10000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px'
  },
  modal: {
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    width: '100%',
    maxWidth: '560px',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '20px 24px',
    borderBottom: '1px solid #e2e8f0',
    backgroundColor: '#f8fafc'
  },
  headerTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    fontSize: '18px',
    fontWeight: '600',
    color: '#1e293b',
    margin: 0
  },
  headerDate: {
    fontSize: '14px',
    color: '#64748b',
    fontWeight: '400'
  },
  closeButton: {
    padding: '8px',
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    color: '#64748b',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease'
  },
  content: {
    flex: 1,
    overflow: 'auto',
    padding: '24px',
    boxSizing: 'border-box'
  },
  section: {
    marginBottom: '24px'
  },
  sectionTitle: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#475569',
    marginBottom: '12px',
    paddingBottom: '8px',
    borderBottom: '1px solid #e2e8f0'
  },
  row: {
    display: 'flex',
    gap: '16px',
    marginBottom: '16px',
    flexWrap: 'wrap'
  },
  row2col: {
    display: 'flex',
    gap: '16px',
    marginBottom: '16px'
  },
  row3col: {
    display: 'flex',
    gap: '12px',
    marginBottom: '16px'
  },
  inputGroup: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    minWidth: 0
  },
  inputGroupHalf: {
    flex: '1 1 45%',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    minWidth: '140px'
  },
  inputGroupThird: {
    flex: '1 1 30%',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    minWidth: '100px'
  },
  label: {
    fontSize: '13px',
    fontWeight: '500',
    color: '#374151'
  },
  required: {
    color: '#dc2626',
    marginLeft: '2px'
  },
  input: {
    padding: '10px 12px',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    fontSize: '14px',
    backgroundColor: '#ffffff',
    transition: 'border-color 0.2s ease',
    outline: 'none',
    boxSizing: 'border-box',
    width: '100%'
  },
  inputError: {
    borderColor: '#dc2626'
  },
  select: {
    padding: '10px 12px',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    fontSize: '14px',
    backgroundColor: '#ffffff',
    cursor: 'pointer',
    boxSizing: 'border-box',
    width: '100%'
  },
  textarea: {
    padding: '10px 12px',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    fontSize: '14px',
    backgroundColor: '#ffffff',
    resize: 'vertical',
    minHeight: '80px',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
    width: '100%'
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    padding: '16px 24px',
    borderTop: '1px solid #e2e8f0',
    backgroundColor: '#f8fafc'
  },
  button: {
    padding: '10px 20px',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '6px'
  },
  cancelButton: {
    backgroundColor: '#ffffff',
    border: '1px solid #d1d5db',
    color: '#374151'
  },
  submitButton: {
    backgroundColor: '#3b82f6',
    border: 'none',
    color: '#ffffff'
  },
  submitButtonDisabled: {
    backgroundColor: '#93c5fd',
    cursor: 'not-allowed'
  },
  errorText: {
    fontSize: '12px',
    color: '#dc2626',
    marginTop: '4px'
  },
  loadingOverlay: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '60px',
    color: '#64748b'
  }
};

const ManualPaymentModal = ({ date, manualId, onClose, onSuccess }) => {
  const navigate = useNavigate();
  const isEditMode = !!manualId;

  // 폼 상태
  const [formData, setFormData] = useState({
    customer_name: "",
    customer_contact: "",
    amount: "",
    fee: "",
    vat: "",
    govt_fee: "",
    channel: "",
    service_type: "trademark",
    tag_id: null,
    description: ""
  });
  const [tags, setTags] = useState([]);
  const [tagTouched, setTagTouched] = useState(false);

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mouseDownOnOverlay, setMouseDownOnOverlay] = useState(false);

  const [alert, setAlert] = useState("");
  const [alertBox, setAlertBox] = useState({
    mode: "",
    context: "",
    bool: false,
  });

  // 세부 금액 합계 (자동 계산용)
  const calculatedTotal = useMemo(() => {
    const fee = parseInt(formData.fee) || 0;
    const vat = parseInt(formData.vat) || 0;
    const govtFee = parseInt(formData.govt_fee) || 0;
    return fee + vat + govtFee;
  }, [formData.fee, formData.vat, formData.govt_fee]);

  // 세부 금액 변경 시 전체 금액 자동 업데이트 (사용자가 직접 수정하지 않은 경우)
  const [amountManuallyEdited, setAmountManuallyEdited] = useState(false);
  const [vatManuallyEdited, setVatManuallyEdited] = useState(false);

  // 수수료 변경 시 부가세 자동 계산 (10%)
  useEffect(() => {
    if (!vatManuallyEdited && !isEditMode) {
      const fee = parseInt(formData.fee) || 0;
      const calculatedVat = Math.round(fee * 0.1);
      setFormData(prev => ({ ...prev, vat: calculatedVat > 0 ? calculatedVat.toString() : "" }));
    }
  }, [formData.fee, vatManuallyEdited, isEditMode]);

  useEffect(() => {
    if (!amountManuallyEdited && !isEditMode) {
      setFormData(prev => ({ ...prev, amount: calculatedTotal > 0 ? calculatedTotal.toString() : "" }));
    }
  }, [calculatedTotal, amountManuallyEdited, isEditMode]);

  // 수정 모드: 기존 데이터 로드
  useEffect(() => {
    if (isEditMode) {
      loadManualPayment();
    }
  }, [manualId]);

  useEffect(() => {
    const loadTags = async () => {
      const result = await getSalesTags();
      if (typeof result === "object" && result?.data) {
        setTags(unwrapResponse(result)?.items || []);
      }
    };
    loadTags();
  }, []);

  // 채널을 고르면 태그를 자동 제안한다. 직접 고른 태그는 덮어쓰지 않는다
  useEffect(() => {
    if (tagTouched || isEditMode || tags.length === 0) return;
    const code = CHANNEL_TO_TAG_CODE[formData.channel] || "other";
    const suggested = tags.find(t => t.code === code);
    if (suggested) {
      setFormData(prev => (prev.tag_id === suggested.id ? prev : { ...prev, tag_id: suggested.id }));
    }
  }, [formData.channel, tags, tagTouched, isEditMode]);

  const loadManualPayment = async () => {
    setIsLoading(true);
    try {
      const result = await getSalesManual(manualId);
      if (typeof result !== "object") {
        return catchError(result, navigate, setAlertBox, setAlert);
      }
      const data = result.data?.data || result.data;
      setFormData({
        customer_name: data.customer_name || "",
        customer_contact: data.customer_contact || "",
        amount: data.amount?.toString() || "",
        fee: data.fee?.toString() || "",
        vat: data.vat?.toString() || "",
        govt_fee: data.govt_fee?.toString() || "",
        channel: data.channel || "",
        service_type: data.service_type || "trademark",
        tag_id: data.tag?.id ?? null,
        description: data.description || ""
      });
      setTagTouched(true);
    } catch (error) {
      catchError(error, navigate, setAlertBox, setAlert);
    } finally {
      setIsLoading(false);
    }
  };

  // 입력 핸들러
  const handleChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // 에러 초기화
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  }, [errors]);

  // 숫자 입력 핸들러 (콤마 포맷팅)
  const handleNumberChange = useCallback((field, value) => {
    // 숫자만 추출
    const numericValue = value.replace(/[^0-9]/g, '');
    handleChange(field, numericValue);
  }, [handleChange]);

  // Validation
  const validate = useCallback(() => {
    const newErrors = {};

    if (!formData.customer_name.trim()) {
      newErrors.customer_name = "고객명을 입력해주세요.";
    }

    if (!formData.amount || parseInt(formData.amount) <= 0) {
      newErrors.amount = "결제 금액을 입력해주세요.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  // 제출 핸들러
  const handleSubmit = async () => {
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      const submitData = {
        customer_name: formData.customer_name.trim(),
        customer_contact: formData.customer_contact.trim() || null,
        amount: parseInt(formData.amount) || 0,
        fee: parseInt(formData.fee) || 0,
        vat: parseInt(formData.vat) || 0,
        govt_fee: parseInt(formData.govt_fee) || 0,
        paid_at: date,
        channel: formData.channel || null,
        service_type: formData.service_type || "trademark",
        tag_id: formData.tag_id || null,
        description: formData.description.trim() || null
      };

      let result;
      if (isEditMode) {
        result = await updateSalesManual(manualId, submitData);
      } else {
        result = await createSalesManual(submitData);
      }

      if (typeof result !== "object") {
        return catchError(result, navigate, setAlertBox, setAlert);
      }

      onSuccess?.();
      onClose();
    } catch (error) {
      catchError(error, navigate, setAlertBox, setAlert);
    } finally {
      setIsSubmitting(false);
    }
  };

  // 오버레이 클릭 핸들링
  const handleOverlayMouseDown = useCallback((e) => {
    if (e.target === e.currentTarget) {
      setMouseDownOnOverlay(true);
    }
  }, []);

  const handleOverlayMouseUp = useCallback((e) => {
    if (e.target === e.currentTarget && mouseDownOnOverlay) {
      onClose();
    }
    setMouseDownOnOverlay(false);
  }, [mouseDownOnOverlay, onClose]);

  // 알림 모달 확인 핸들러
  const handleAlertOk = useCallback(() => {
    if (alert === "logout") navigate("/");
  }, [alert, navigate]);

  return (
    <>
      <div
        style={styles.overlay}
        onMouseDown={handleOverlayMouseDown}
        onMouseUp={handleOverlayMouseUp}
      >
        <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
          {/* 헤더 */}
          <div style={styles.header}>
            <h2 style={styles.headerTitle}>
              {isEditMode ? <FaEdit /> : <FaPlus />}
              {isEditMode ? "수동 결제 수정" : "수동 결제 등록"}
              <span style={styles.headerDate}>{formatDateDisplay(date)}</span>
            </h2>
            <button
              style={styles.closeButton}
              onClick={onClose}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#e2e8f0';
                e.currentTarget.style.color = '#1e293b';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#64748b';
              }}
            >
              <FaTimes size={18} />
            </button>
          </div>

          {/* 콘텐츠 */}
          <div style={styles.content}>
            {isLoading ? (
              <div style={styles.loadingOverlay}>데이터 로딩 중...</div>
            ) : (
              <>
                {/* 고객 정보 */}
                <div style={styles.section}>
                  <div style={styles.sectionTitle}>고객 정보</div>
                  <div style={styles.row2col}>
                    <div style={styles.inputGroupHalf}>
                      <label style={styles.label}>
                        고객명<span style={styles.required}>*</span>
                      </label>
                      <input
                        type="text"
                        style={{
                          ...styles.input,
                          ...(errors.customer_name ? styles.inputError : {})
                        }}
                        value={formData.customer_name}
                        onChange={(e) => handleChange('customer_name', e.target.value)}
                        placeholder="고객명 입력"
                      />
                      {errors.customer_name && (
                        <span style={styles.errorText}>{errors.customer_name}</span>
                      )}
                    </div>
                    <div style={styles.inputGroupHalf}>
                      <label style={styles.label}>연락처 (선택)</label>
                      <input
                        type="text"
                        style={styles.input}
                        value={formData.customer_contact}
                        onChange={(e) => handleChange('customer_contact', e.target.value)}
                        placeholder="010-0000-0000"
                      />
                    </div>
                  </div>
                </div>

                {/* 금액 정보 */}
                <div style={styles.section}>
                  <div style={styles.sectionTitle}>금액 정보</div>

                  {/* 수수료 + 부가세 그룹 */}
                  <div style={{ marginBottom: '16px' }}>
                    <div style={styles.inputGroup}>
                      <label style={styles.label}>수수료</label>
                      <input
                        type="text"
                        style={styles.input}
                        value={formData.fee ? parseInt(formData.fee).toLocaleString() : ''}
                        onChange={(e) => handleNumberChange('fee', e.target.value)}
                        placeholder="0"
                      />
                    </div>
                    {/* 부가세 - ㄴ자 연결선 */}
                    <div style={{ display: 'flex', alignItems: 'flex-start', marginTop: '8px', marginLeft: '8px' }}>
                      {/* ㄴ자 모양 */}
                      <div style={{
                        width: '16px',
                        height: '28px',
                        borderLeft: '2px solid #d1d5db',
                        borderBottom: '2px solid #d1d5db',
                        borderBottomLeftRadius: '6px',
                        marginRight: '10px',
                        flexShrink: 0
                      }} />
                      <div style={{ flex: 1 }}>
                        <label style={{ ...styles.label, fontSize: '12px', color: '#6b7280' }}>
                          부가세
                          {!vatManuallyEdited && formData.fee && (
                            <span style={{ fontSize: '10px', color: '#9ca3af', marginLeft: '6px' }}>(10% 자동)</span>
                          )}
                        </label>
                        <input
                          type="text"
                          style={{ ...styles.input, backgroundColor: '#f9fafb', marginTop: '4px' }}
                          value={formData.vat ? parseInt(formData.vat).toLocaleString() : ''}
                          onChange={(e) => {
                            handleNumberChange('vat', e.target.value);
                            setVatManuallyEdited(true);
                          }}
                          placeholder="0"
                        />
                      </div>
                    </div>
                  </div>

                  {/* 관납료 - 별도 항목 */}
                  <div style={{ marginBottom: '16px' }}>
                    <div style={styles.inputGroup}>
                      <label style={styles.label}>관납료</label>
                      <input
                        type="text"
                        style={styles.input}
                        value={formData.govt_fee ? parseInt(formData.govt_fee).toLocaleString() : ''}
                        onChange={(e) => handleNumberChange('govt_fee', e.target.value)}
                        placeholder="0"
                      />
                    </div>
                  </div>

                  {/* 총 결제 금액 (자동 계산 + 수정 가능) */}
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>
                      총 결제 금액<span style={styles.required}>*</span>
                      {!amountManuallyEdited && calculatedTotal > 0 && (
                        <span style={{ fontSize: '11px', color: '#6b7280', marginLeft: '8px', fontWeight: '400' }}>
                          (자동 계산됨)
                        </span>
                      )}
                    </label>
                    <input
                      type="text"
                      style={{
                        ...styles.input,
                        ...(errors.amount ? styles.inputError : {})
                      }}
                      value={formData.amount ? parseInt(formData.amount).toLocaleString() : ''}
                      onChange={(e) => {
                        handleNumberChange('amount', e.target.value);
                        setAmountManuallyEdited(true);
                      }}
                      placeholder="0"
                    />
                    {errors.amount && (
                      <span style={styles.errorText}>{errors.amount}</span>
                    )}
                  </div>
                </div>

                {/* 추가 정보 */}
                <div style={styles.section}>
                  <div style={styles.sectionTitle}>추가 정보</div>

                  <div style={styles.row2col}>
                    <div style={styles.inputGroup}>
                      <label style={styles.label}>서비스 구분</label>
                      <select
                        style={styles.select}
                        value={formData.service_type}
                        onChange={(e) => handleChange('service_type', e.target.value)}
                      >
                        {SERVICE_TYPE_OPTIONS.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div style={styles.inputGroup}>
                      <label style={styles.label}>결제 채널 (선택)</label>
                      <select
                        style={styles.select}
                        value={formData.channel}
                        onChange={(e) => handleChange('channel', e.target.value)}
                      >
                        {CHANNEL_OPTIONS.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div style={{ marginBottom: '16px' }}>
                    <div style={styles.inputGroup}>
                      <label style={styles.label}>매출 태그</label>
                      <div style={{ padding: '4px 0' }}>
                        <SalesTagSelect
                          tags={tags}
                          value={formData.tag_id}
                          onChange={(tagId) => { setTagTouched(true); handleChange('tag_id', tagId); }}
                          ariaLabel="매출 태그"
                        />
                      </div>
                      <span style={{ fontSize: '11px', color: '#9ca3af' }}>채널을 고르면 자동으로 제안됩니다. 직접 바꿀 수 있습니다.</span>
                    </div>
                  </div>

                  <div style={styles.inputGroup}>
                    <label style={styles.label}>메모 (선택)</label>
                    <textarea
                      style={styles.textarea}
                      value={formData.description}
                      onChange={(e) => handleChange('description', e.target.value)}
                      placeholder="추가 메모를 입력하세요"
                    />
                  </div>
                </div>
              </>
            )}
          </div>

          {/* 푸터 */}
          <div style={styles.footer}>
            <button
              style={{ ...styles.button, ...styles.cancelButton }}
              onClick={onClose}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ffffff'}
            >
              취소
            </button>
            <button
              style={{
                ...styles.button,
                ...styles.submitButton,
                ...(isSubmitting ? styles.submitButtonDisabled : {})
              }}
              onClick={handleSubmit}
              disabled={isSubmitting || isLoading}
              onMouseEnter={(e) => {
                if (!isSubmitting) e.currentTarget.style.backgroundColor = '#2563eb';
              }}
              onMouseLeave={(e) => {
                if (!isSubmitting) e.currentTarget.style.backgroundColor = '#3b82f6';
              }}
            >
              {isSubmitting ? "처리 중..." : (isEditMode ? "수정하기" : "등록하기")}
            </button>
          </div>
        </div>
      </div>

      {/* 공통 알림 모달 */}
      {alertBox.bool && (
        <CommonModal
          setModal={setAlertBox}
          modal={alertBox}
          okFn={handleAlertOk}
        />
      )}
    </>
  );
};

export default ManualPaymentModal;
